/**
 * Light normalization for input alias storage and lookup.
 * Only normalizes syntactic noise (case, whitespace) - NOT abbreviations.
 *
 * @see location-matching-algorithm.md#input-alias-normalization
 */
export function normalizeInputForAlias(input: string): string {
  return input
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ')
}

/**
 * Full normalization for address comparison scoring.
 * Expands abbreviations for consistent comparison with legacy data.
 *
 * @see location-matching-algorithm.md#normalization-for-comparison
 */
export function normalizeForComparison(input: string): string {
  let normalized = input
    .toLowerCase()
    .trim()
    .replace(/[.,-]/g, '')
    .replace(/\s+/g, ' ')

  const streetAbbreviations: Record<string, string> = {
    'st': 'street',
    'ave': 'avenue',
    'blvd': 'boulevard',
    'dr': 'drive',
    'ln': 'lane',
    'rd': 'road',
    'ct': 'court',
    'cir': 'circle',
    'pl': 'place',
    'ter': 'terrace',
    'hwy': 'highway',
    'pkwy': 'parkway',
    'ste': 'suite',
    'apt': 'apartment',
    'bldg': 'building',
  }

  const directionals: Record<string, string> = {
    'n': 'north',
    's': 'south',
    'e': 'east',
    'w': 'west',
    'ne': 'northeast',
    'nw': 'northwest',
    'se': 'southeast',
    'sw': 'southwest',
  }

  for (const [abbr, full] of Object.entries(streetAbbreviations)) {
    normalized = normalized.replace(new RegExp(`\\b${abbr}\\b`, 'g'), full)
  }
  for (const [abbr, full] of Object.entries(directionals)) {
    normalized = normalized.replace(new RegExp(`\\b${abbr}\\b`, 'g'), full)
  }

  return normalized
}

/**
 * Full normalization for business name comparison.
 */
export function normalizeNameForComparison(input: string): string {
  let normalized = normalizeForComparison(input)

  const businessSuffixes = ['llc', 'inc', 'corp', 'ltd', 'co', 'company', 'corporation']
  for (const suffix of businessSuffixes) {
    normalized = normalized.replace(new RegExp(`\\b${suffix}\\b`, 'g'), '')
  }

  return normalized.trim().replace(/\s+/g, ' ')
}

/**
 * Extract and normalize suite/unit number from address line 2.
 *
 * @see location-matching-algorithm.md#suite-normalization
 */
export function normalizeSuite(addressLineTwo: string | null | undefined): string | null {
  if (!addressLineTwo) return null

  return addressLineTwo
    .toLowerCase()
    .replace(/^(suite|ste|unit|apt|apartment|building|bldg|#)\s*/i, '')
    .replace(/[^a-z0-9]/g, '')
    .trim() || null
}
